/**
 * diagnosisService.js
 * 
 * ICD-10 behavioral health code lookup, diagnosis management,
 * medication tracking, and allergy management.
 * 
 * All data is stored directly on the sharedIndividuals document
 * in Firestore (no separate collection needed).
 * 
 * Fields on sharedIndividuals:
 *   diagnoses: [{ code, description, type, diagnosedBy, date, status }]
 *   medications: [{ name, dosage, frequency, route, prescriber, startDate, endDate, status, refillDate, notes }]
 *   allergies: [{ allergen, reaction, severity, reportedDate }]
 */

import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";
import { orgCollection, orgDoc } from "./orgPaths";
import { logAuditEvent } from "./auditService";

// ─── ICD-10 Behavioral Health Codes (F-codes + common co-occurring) ──────

export const ICD10_CODES = [
  // Mood Disorders
  { code: "F32.0", description: "Major depressive disorder, single episode, mild", category: "Depressive Disorders" },
  { code: "F32.1", description: "Major depressive disorder, single episode, moderate", category: "Depressive Disorders" },
  { code: "F32.2", description: "Major depressive disorder, single episode, severe without psychotic features", category: "Depressive Disorders" },
  { code: "F32.3", description: "Major depressive disorder, single episode, severe with psychotic features", category: "Depressive Disorders" },
  { code: "F32.4", description: "Major depressive disorder, single episode, in partial remission", category: "Depressive Disorders" },
  { code: "F32.5", description: "Major depressive disorder, single episode, in full remission", category: "Depressive Disorders" },
  { code: "F32.9", description: "Major depressive disorder, single episode, unspecified", category: "Depressive Disorders" },
  { code: "F33.0", description: "Major depressive disorder, recurrent, mild", category: "Depressive Disorders" },
  { code: "F33.1", description: "Major depressive disorder, recurrent, moderate", category: "Depressive Disorders" },
  { code: "F33.2", description: "Major depressive disorder, recurrent, severe without psychotic features", category: "Depressive Disorders" },
  { code: "F33.3", description: "Major depressive disorder, recurrent, severe with psychotic features", category: "Depressive Disorders" },
  { code: "F33.9", description: "Major depressive disorder, recurrent, unspecified", category: "Depressive Disorders" },
  { code: "F34.1", description: "Dysthymic disorder (Persistent depressive disorder)", category: "Depressive Disorders" },

  // Bipolar
  { code: "F31.0", description: "Bipolar disorder, current episode hypomanic", category: "Bipolar Disorders" },
  { code: "F31.1", description: "Bipolar disorder, current episode manic without psychotic features", category: "Bipolar Disorders" },
  { code: "F31.2", description: "Bipolar disorder, current episode manic with psychotic features", category: "Bipolar Disorders" },
  { code: "F31.3", description: "Bipolar disorder, current episode depressed, mild or moderate", category: "Bipolar Disorders" },
  { code: "F31.4", description: "Bipolar disorder, current episode depressed, severe without psychotic features", category: "Bipolar Disorders" },
  { code: "F31.5", description: "Bipolar disorder, current episode depressed, severe with psychotic features", category: "Bipolar Disorders" },
  { code: "F31.9", description: "Bipolar disorder, unspecified", category: "Bipolar Disorders" },

  // Anxiety Disorders
  { code: "F41.0", description: "Panic disorder without agoraphobia", category: "Anxiety Disorders" },
  { code: "F41.1", description: "Generalized anxiety disorder", category: "Anxiety Disorders" },
  { code: "F40.10", description: "Social anxiety disorder (social phobia)", category: "Anxiety Disorders" },
  { code: "F40.00", description: "Agoraphobia, unspecified", category: "Anxiety Disorders" },
  { code: "F42.2", description: "Obsessive-compulsive disorder, mixed", category: "Anxiety Disorders" },
  { code: "F42.9", description: "Obsessive-compulsive disorder, unspecified", category: "Anxiety Disorders" },

  // PTSD / Trauma
  { code: "F43.10", description: "Post-traumatic stress disorder, unspecified", category: "Trauma & Stressor-Related" },
  { code: "F43.11", description: "Post-traumatic stress disorder, acute", category: "Trauma & Stressor-Related" },
  { code: "F43.12", description: "Post-traumatic stress disorder, chronic", category: "Trauma & Stressor-Related" },
  { code: "F43.20", description: "Adjustment disorder, unspecified", category: "Trauma & Stressor-Related" },
  { code: "F43.21", description: "Adjustment disorder with depressed mood", category: "Trauma & Stressor-Related" },
  { code: "F43.22", description: "Adjustment disorder with anxiety", category: "Trauma & Stressor-Related" },
  { code: "F43.23", description: "Adjustment disorder with mixed anxiety and depressed mood", category: "Trauma & Stressor-Related" },
  { code: "F43.25", description: "Adjustment disorder with mixed disturbance of emotions and conduct", category: "Trauma & Stressor-Related" },

  // Schizophrenia Spectrum
  { code: "F20.0", description: "Paranoid schizophrenia", category: "Schizophrenia Spectrum" },
  { code: "F20.1", description: "Disorganized schizophrenia", category: "Schizophrenia Spectrum" },
  { code: "F20.9", description: "Schizophrenia, unspecified", category: "Schizophrenia Spectrum" },
  { code: "F25.0", description: "Schizoaffective disorder, bipolar type", category: "Schizophrenia Spectrum" },
  { code: "F25.1", description: "Schizoaffective disorder, depressive type", category: "Schizophrenia Spectrum" },
  { code: "F25.9", description: "Schizoaffective disorder, unspecified", category: "Schizophrenia Spectrum" },

  // Substance Use Disorders
  { code: "F10.10", description: "Alcohol use disorder, mild", category: "Substance Use Disorders" },
  { code: "F10.20", description: "Alcohol use disorder, moderate/severe", category: "Substance Use Disorders" },
  { code: "F10.21", description: "Alcohol dependence, in remission", category: "Substance Use Disorders" },
  { code: "F11.10", description: "Opioid use disorder, mild", category: "Substance Use Disorders" },
  { code: "F11.20", description: "Opioid use disorder, moderate/severe", category: "Substance Use Disorders" },
  { code: "F11.21", description: "Opioid dependence, in remission", category: "Substance Use Disorders" },
  { code: "F12.10", description: "Cannabis use disorder, mild", category: "Substance Use Disorders" },
  { code: "F12.20", description: "Cannabis use disorder, moderate/severe", category: "Substance Use Disorders" },
  { code: "F14.10", description: "Cocaine use disorder, mild", category: "Substance Use Disorders" },
  { code: "F14.20", description: "Cocaine use disorder, moderate/severe", category: "Substance Use Disorders" },
  { code: "F15.10", description: "Stimulant use disorder (amphetamine), mild", category: "Substance Use Disorders" },
  { code: "F15.20", description: "Stimulant use disorder (amphetamine), moderate/severe", category: "Substance Use Disorders" },
  { code: "F19.10", description: "Other psychoactive substance use disorder, mild", category: "Substance Use Disorders" },
  { code: "F19.20", description: "Other psychoactive substance use disorder, moderate/severe", category: "Substance Use Disorders" },

  // ADHD
  { code: "F90.0", description: "ADHD, predominantly inattentive type", category: "Neurodevelopmental" },
  { code: "F90.1", description: "ADHD, predominantly hyperactive-impulsive type", category: "Neurodevelopmental" },
  { code: "F90.2", description: "ADHD, combined type", category: "Neurodevelopmental" },
  { code: "F90.9", description: "ADHD, unspecified type", category: "Neurodevelopmental" },

  // Autism Spectrum
  { code: "F84.0", description: "Autistic disorder", category: "Neurodevelopmental" },

  // Personality Disorders
  { code: "F60.3", description: "Borderline personality disorder", category: "Personality Disorders" },
  { code: "F60.2", description: "Antisocial personality disorder", category: "Personality Disorders" },
  { code: "F60.9", description: "Personality disorder, unspecified", category: "Personality Disorders" },

  // Eating Disorders
  { code: "F50.00", description: "Anorexia nervosa, unspecified", category: "Eating Disorders" },
  { code: "F50.02", description: "Anorexia nervosa, binge eating/purging type", category: "Eating Disorders" },
  { code: "F50.2", description: "Bulimia nervosa", category: "Eating Disorders" },
  { code: "F50.81", description: "Binge eating disorder", category: "Eating Disorders" },

  // Sleep Disorders
  { code: "F51.01", description: "Primary insomnia", category: "Sleep Disorders" },
  { code: "G47.00", description: "Insomnia, unspecified", category: "Sleep Disorders" },

  // Common Co-Occurring Medical
  { code: "E11.9", description: "Type 2 diabetes mellitus without complications", category: "Medical Co-occurring" },
  { code: "I10", description: "Essential hypertension", category: "Medical Co-occurring" },
  { code: "E66.9", description: "Obesity, unspecified", category: "Medical Co-occurring" },
  { code: "J45.909", description: "Asthma, unspecified, uncomplicated", category: "Medical Co-occurring" },
  { code: "M54.5", description: "Low back pain", category: "Medical Co-occurring" },
  { code: "G43.909", description: "Migraine, unspecified", category: "Medical Co-occurring" },
  { code: "K21.0", description: "GERD with esophagitis", category: "Medical Co-occurring" },
  { code: "E78.5", description: "Hyperlipidemia, unspecified", category: "Medical Co-occurring" },
  { code: "B20", description: "HIV disease", category: "Medical Co-occurring" },
  { code: "G40.909", description: "Epilepsy, unspecified", category: "Medical Co-occurring" },
];

// ─── Search Functions ────────────────────────────────────────────

/**
 * Search ICD-10 codes by code or description text.
 * Returns top 15 matches.
 */
export function searchICD10(query) {
  if (!query || query.trim().length < 2) return [];
  const q = query.toLowerCase().trim();

  return ICD10_CODES
    .filter(dx =>
      dx.code.toLowerCase().includes(q) ||
      dx.description.toLowerCase().includes(q) ||
      dx.category.toLowerCase().includes(q)
    )
    .slice(0, 15);
}

/**
 * Get all codes in a category.
 */
export function getCodesByCategory(category) {
  return ICD10_CODES.filter(dx => dx.category === category);
}

/**
 * Get unique categories.
 */
export function getCategories() {
  return [...new Set(ICD10_CODES.map(dx => dx.category))];
}

/**
 * Look up a single code.
 */
export function lookupCode(code) {
  return ICD10_CODES.find(dx => dx.code === code) || null;
}


// ─── Diagnosis CRUD (updates sharedIndividuals doc) ──────────────

/**
 * Add a diagnosis to an individual.
 */
export async function addDiagnosis(individualId, diagnosis) {
  const ref = orgDoc("individuals", individualId);
  const entry = {
    code: diagnosis.code,
    description: diagnosis.description,
    category: diagnosis.category || "",
    type: diagnosis.type || "secondary", // primary, secondary, tertiary
    diagnosedBy: diagnosis.diagnosedBy || "",
    date: diagnosis.date || new Date().toISOString().split("T")[0],
    status: "active",
    addedAt: new Date().toISOString(),
  };

  // We need to read current diagnoses, add, and write back
  // Using arrayUnion won't work for objects, so we use updateDoc with the full array
  // The caller should pass current diagnoses array
  const currentDiagnoses = diagnosis._currentList || [];
  const updated = [...currentDiagnoses, entry];

  await updateDoc(ref, {
    diagnoses: updated,
    updatedAt: serverTimestamp(),
  });

  try {
    await logAuditEvent("DIAGNOSIS_ADDED", {
      individualId,
      code: entry.code,
      description: entry.description,
      type: entry.type,
    });
  } catch (e) { /* audit best-effort */ }

  return entry;
}

/**
 * Update a diagnosis (e.g., change type or resolve).
 */
export async function updateDiagnosis(individualId, diagnosisIndex, updates, currentDiagnoses) {
  const ref = orgDoc("individuals", individualId);
  const updated = [...currentDiagnoses];
  updated[diagnosisIndex] = { ...updated[diagnosisIndex], ...updates };

  await updateDoc(ref, {
    diagnoses: updated,
    updatedAt: serverTimestamp(),
  });

  try {
    await logAuditEvent("DIAGNOSIS_UPDATED", {
      individualId,
      code: updated[diagnosisIndex].code,
      changes: Object.keys(updates),
    });
  } catch (e) {}
}

/**
 * Resolve (deactivate) a diagnosis.
 */
export async function resolveDiagnosis(individualId, diagnosisIndex, currentDiagnoses) {
  return updateDiagnosis(individualId, diagnosisIndex, {
    status: "resolved",
    resolvedDate: new Date().toISOString().split("T")[0],
  }, currentDiagnoses);
}


// ─── Medication CRUD ─────────────────────────────────────────────

/**
 * Add a medication.
 */
export async function addMedication(individualId, medication, currentMedications = []) {
  const ref = orgDoc("individuals", individualId);
  const entry = {
    name: medication.name,
    dosage: medication.dosage || "",
    frequency: medication.frequency || "",
    route: medication.route || "oral",
    prescriber: medication.prescriber || "",
    startDate: medication.startDate || new Date().toISOString().split("T")[0],
    endDate: null,
    status: "active",
    refillDate: medication.refillDate || "",
    notes: medication.notes || "",
    addedAt: new Date().toISOString(),
  };

  const updated = [...currentMedications, entry];

  await updateDoc(ref, {
    medications: updated,
    updatedAt: serverTimestamp(),
  });

  try {
    await logAuditEvent("MEDICATION_ADDED", {
      individualId,
      medication: entry.name,
      dosage: entry.dosage,
    });
  } catch (e) {}

  return entry;
}

/**
 * Update a medication.
 */
export async function updateMedication(individualId, medIndex, updates, currentMedications) {
  const ref = orgDoc("individuals", individualId);
  const updated = [...currentMedications];
  updated[medIndex] = { ...updated[medIndex], ...updates };

  await updateDoc(ref, {
    medications: updated,
    updatedAt: serverTimestamp(),
  });

  try {
    await logAuditEvent("MEDICATION_UPDATED", {
      individualId,
      medication: updated[medIndex].name,
      changes: Object.keys(updates),
    });
  } catch (e) {}
}

/**
 * Discontinue a medication.
 */
export async function discontinueMedication(individualId, medIndex, reason, currentMedications) {
  return updateMedication(individualId, medIndex, {
    status: "discontinued",
    endDate: new Date().toISOString().split("T")[0],
    discontinueReason: reason || "",
  }, currentMedications);
}


// ─── Allergy CRUD ────────────────────────────────────────────────

/**
 * Add an allergy.
 */
export async function addAllergy(individualId, allergy, currentAllergies = []) {
  const ref = orgDoc("individuals", individualId);
  const entry = {
    allergen: allergy.allergen,
    reaction: allergy.reaction || "",
    severity: allergy.severity || "moderate", // mild, moderate, severe
    reportedDate: new Date().toISOString().split("T")[0],
  };

  const updated = [...currentAllergies, entry];

  await updateDoc(ref, {
    allergies: updated,
    updatedAt: serverTimestamp(),
  });

  try {
    await logAuditEvent("ALLERGY_ADDED", { individualId, allergen: entry.allergen });
  } catch (e) {}

  return entry;
}

/**
 * Remove an allergy.
 */
export async function removeAllergy(individualId, allergyIndex, currentAllergies) {
  const ref = orgDoc("individuals", individualId);
  const updated = currentAllergies.filter((_, i) => i !== allergyIndex);

  await updateDoc(ref, {
    allergies: updated,
    updatedAt: serverTimestamp(),
  });
}


// ─── Common Medications for Behavioral Health (autocomplete) ─────

export const COMMON_MEDICATIONS = [
  // SSRIs
  "Sertraline (Zoloft)", "Fluoxetine (Prozac)", "Escitalopram (Lexapro)",
  "Citalopram (Celexa)", "Paroxetine (Paxil)", "Fluvoxamine (Luvox)",
  // SNRIs
  "Venlafaxine (Effexor)", "Duloxetine (Cymbalta)", "Desvenlafaxine (Pristiq)",
  // Atypical Antidepressants
  "Bupropion (Wellbutrin)", "Mirtazapine (Remeron)", "Trazodone (Desyrel)",
  // Mood Stabilizers
  "Lithium (Lithobid)", "Valproic Acid (Depakote)", "Lamotrigine (Lamictal)",
  "Carbamazepine (Tegretol)", "Oxcarbazepine (Trileptal)",
  // Antipsychotics
  "Risperidone (Risperdal)", "Olanzapine (Zyprexa)", "Quetiapine (Seroquel)",
  "Aripiprazole (Abilify)", "Ziprasidone (Geodon)", "Clozapine (Clozaril)",
  "Lurasidone (Latuda)", "Paliperidone (Invega)", "Haloperidol (Haldol)",
  "Fluphenazine (Prolixin)",
  // Anxiolytics
  "Buspirone (Buspar)", "Hydroxyzine (Vistaril)", "Gabapentin (Neurontin)",
  "Pregabalin (Lyrica)",
  // Benzodiazepines
  "Lorazepam (Ativan)", "Clonazepam (Klonopin)", "Diazepam (Valium)",
  "Alprazolam (Xanax)",
  // ADHD
  "Methylphenidate (Ritalin/Concerta)", "Amphetamine/Dextroamphetamine (Adderall)",
  "Lisdexamfetamine (Vyvanse)", "Atomoxetine (Strattera)", "Guanfacine (Intuniv)",
  // MAT (Medication-Assisted Treatment)
  "Buprenorphine/Naloxone (Suboxone)", "Naltrexone (Vivitrol)",
  "Methadone", "Acamprosate (Campral)", "Disulfiram (Antabuse)",
  // Sleep
  "Melatonin", "Zolpidem (Ambien)", "Eszopiclone (Lunesta)",
  // PRN / Other
  "Diphenhydramine (Benadryl)", "Propranolol (Inderal)",
  "Clonidine (Catapres)", "Prazosin (Minipress)",
];

/**
 * Search common medications for autocomplete.
 */
export function searchMedications(query) {
  if (!query || query.trim().length < 2) return [];
  const q = query.toLowerCase().trim();
  return COMMON_MEDICATIONS.filter(m => m.toLowerCase().includes(q)).slice(0, 10);
}

// ─── Frequency Options ───────────────────────────────────────────

export const FREQUENCY_OPTIONS = [
  "Once daily (QD)",
  "Twice daily (BID)",
  "Three times daily (TID)",
  "Four times daily (QID)",
  "Every morning (QAM)",
  "Every evening (QPM)",
  "At bedtime (QHS)",
  "As needed (PRN)",
  "Weekly",
  "Biweekly",
  "Monthly (IM injection)",
];

export const ROUTE_OPTIONS = [
  "Oral",
  "Sublingual",
  "Intramuscular (IM)",
  "Subcutaneous (SubQ)",
  "Intravenous (IV)",
  "Topical",
  "Transdermal patch",
  "Inhaled",
];

export const ALLERGY_SEVERITIES = ["Mild", "Moderate", "Severe", "Life-threatening"];